/**
 * Persistent multi-day cart for the SCHOOL_ADMIN bulk-ordering surface
 * (/orders/school). Replaces the old per-(day,mealtime) one-shot submit:
 * quantities entered across any number of days + mealtimes accumulate
 * here and are submitted together from the full-page Review & checkout.
 *
 * Zustand + `persist` to **sessionStorage** — the cart survives in-app
 * navigation + reloads within a session but doesn't linger across browser
 * sessions (a half-built bulk order shouldn't resurrect days later).
 *
 * Lines are bucketed by `locationId` so a multi-campus admin's carts stay
 * isolated. The pure cart model (keys, upsert, grouping, totals) lives in
 * `@/lib/school-orders/cart` so it's unit-testable without this store.
 */

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import {
  buildCheckoutPlan,
  type CheckoutGroup,
  groupKey,
  type SchoolCartLine,
  subtotalCents,
  totalQty,
  upsertLine,
} from '@/lib/school-orders/cart'

interface SchoolCartState {
  /** Cart lines bucketed by service-location id. */
  linesByLocation: Record<string, SchoolCartLine[]>
  /** Insert / update / remove a line (quantity <= 0 removes it). */
  setLine: (line: SchoolCartLine) => void
  /**
   * Atomically replace ALL cart lines for one (location, service date) with
   * `lines` — the "Add meals to cart" / "Update cart" commit. Drops the
   * day's prior lines (across every mealtime) and inserts the supplied
   * ones, so committing the same sheet twice is a no-op. (The commit used
   * to SUM into the cart; with the inputs cleared after an add, admins
   * re-entered their quantities and doubled their orders — Larkspur,
   * launch week.)
   */
  setDayLines: (locationId: string, date: string, lines: SchoolCartLine[]) => void
  /** Drop every line for a location (e.g. "Clear cart"). */
  clearLocation: (locationId: string) => void
  /**
   * Drop the lines belonging to a set of `${date}::${mealtime}` groups —
   * post-submit cleanup so successfully-submitted groups leave the cart
   * while any that failed stay for a retry.
   */
  clearGroups: (locationId: string, groupKeys: ReadonlySet<string>) => void
  /**
   * Drop a `${date}::${mealtime}` group's lines for specific menu items —
   * pruning stale lines the submit rejected with
   * `menu_item_not_available_on_date` (the menu changed after the cart
   * was filled), so the group's remaining lines can resubmit cleanly.
   */
  removeMenuItemLines: (locationId: string, groupKeyToPrune: string, menuItemIds: ReadonlySet<string>) => void
  /**
   * Patch `unitPriceCents` to the fresh submit-time resolve — the 409
   * `price_changed` consent loop (mirrors the family cart store). Runs
   * on both dialog outcomes so the cart never shows a stale figure.
   */
  applyPriceChanges: (locationId: string, priceByMenuItemId: ReadonlyMap<string, number>) => void
}

export const useSchoolCart = create<SchoolCartState>()(
  persist(
    (set, get) => ({
      linesByLocation: {},
      setLine: (line) => {
        const current = get().linesByLocation[line.locationId] ?? []
        const next = upsertLine(current, line)
        set({ linesByLocation: { ...get().linesByLocation, [line.locationId]: next } })
      },
      setDayLines: (locationId, date, lines) => {
        const current = get().linesByLocation[locationId] ?? []
        const kept = current.filter((l) => l.date !== date)
        const next = [...kept, ...lines.filter((l) => l.quantity > 0)]
        set({ linesByLocation: { ...get().linesByLocation, [locationId]: next } })
      },
      clearLocation: (locationId) => {
        const next = { ...get().linesByLocation }
        delete next[locationId]
        set({ linesByLocation: next })
      },
      clearGroups: (locationId, groupKeys) => {
        const current = get().linesByLocation[locationId] ?? []
        const kept = current.filter((l) => !groupKeys.has(groupKey(l.date, l.mealtime)))
        set({ linesByLocation: { ...get().linesByLocation, [locationId]: kept } })
      },
      removeMenuItemLines: (locationId, groupKeyToPrune, menuItemIds) => {
        const current = get().linesByLocation[locationId] ?? []
        const kept = current.filter(
          (l) => groupKey(l.date, l.mealtime) !== groupKeyToPrune || !menuItemIds.has(l.menuItemId),
        )
        set({ linesByLocation: { ...get().linesByLocation, [locationId]: kept } })
      },
      applyPriceChanges: (locationId, priceByMenuItemId) => {
        const current = get().linesByLocation[locationId] ?? []
        const next = current.map((l) => {
          const newCents = priceByMenuItemId.get(l.menuItemId)
          return newCents === undefined ? l : { ...l, unitPriceCents: newCents }
        })
        set({ linesByLocation: { ...get().linesByLocation, [locationId]: next } })
      },
    }),
    {
      name: 'ordo.school-cart',
      storage: createJSONStorage(() => sessionStorage),
      // v0 → v1: component customization moved from PERCENTAGES to
      // QUANTITIES. A school mid-order when this deploys rehydrates v0
      // lines; without the convert, `{VEGETABLE: 50}` would be read as 50
      // units instead of 50 percent. See `migrateCartLine`.
      //
      // v1 → v2: carts written before the replace-semantics fix (#1835) can
      // hold DOUBLED lines from the summing "Add meals to cart" — and since
      // the sheet now mirrors the cart, a stale cart re-surfaces the doubled
      // quantities as if the bug were still live (Mark's report, 2026-08-07;
      // sessionStorage survives hard refreshes, so only a tab close cleared
      // it). Pre-fix carts cannot be trusted line-by-line: drop them and let
      // the school re-enter under replace semantics.
      version: 2,
      migrate: (persisted, version) =>
        version >= 2 ? (persisted as SchoolCartState) : { ...(persisted as SchoolCartState), linesByLocation: {} },
    },
  ),
)

// ---------------------------------------------------------------------------
// Selectors — call with `useSchoolCart(selectLinesFor(locationId))` so the
// component re-renders only when that location's slice changes.
// ---------------------------------------------------------------------------

const EMPTY: SchoolCartLine[] = []

export const selectLinesFor = (locationId: string) => (s: SchoolCartState) => s.linesByLocation[locationId] ?? EMPTY

export function linesFor(state: SchoolCartState, locationId: string): SchoolCartLine[] {
  return state.linesByLocation[locationId] ?? EMPTY
}

export function cartTotalQty(state: SchoolCartState, locationId: string): number {
  return totalQty(linesFor(state, locationId))
}

export function cartSubtotalCents(state: SchoolCartState, locationId: string): number | null {
  return subtotalCents(linesFor(state, locationId))
}

export function cartGroupsFor(state: SchoolCartState, locationId: string): CheckoutGroup[] {
  return buildCheckoutPlan(linesFor(state, locationId))
}
